// WellDeviation.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '../api/api';
import { DataForm } from '../components/forms';
import { DataTable } from '../components/tables';
import WellPath2D from '../components/WellPath2D';
import WellPath3D from '../components/WellPath3D';
import './WellDeviation.css';

const WellDeviation = ({ wellId: propWellId }) => {
    const [searchParams] = useSearchParams();
    const urlWellId = searchParams.get('wellId');
    const initialWellId = propWellId || urlWellId;
    
    // Core state
    const [wells, setWells] = useState([]);
    const [selectedWellId, setSelectedWellId] = useState(initialWellId || '');
    const [selectedWell, setSelectedWell] = useState(null);
    const [surveyHeaders, setSurveyHeaders] = useState([]);
    const [selectedHeaderId, setSelectedHeaderId] = useState('');
    const [selectedHeader, setSelectedHeader] = useState(null);
    const [surveyPoints, setSurveyPoints] = useState([]);
    
    // Column configurations
    const [surveyPointColumns, setSurveyPointColumns] = useState([]);
    const [surveyHeaderColumns, setSurveyHeaderColumns] = useState([]);
    
    // UI state
    const [isLoading, setIsLoading] = useState(false);
    const [toast, setToast] = useState('');
    const [activeTab, setActiveTab] = useState('overview');
    const [showDataForm, setShowDataForm] = useState(false);
    const [dataFormConfig, setDataFormConfig] = useState({});
    const [editingRecord, setEditingRecord] = useState(null);
    
    // Survey header management
    const [selectedSection, setSelectedSection] = useState(null);
    const [selectedRecord, setSelectedRecord] = useState(null);
    
    // Dual table form state for deviation survey
    const [showDualTableForm, setShowDualTableForm] = useState(false);
    const [dualTableConfig, setDualTableConfig] = useState({});
    
    // Visualization state
    const [currentView, setCurrentView] = useState('table');
    const [calculatedData, setCalculatedData] = useState([]);
    const [showCalculationConfirm, setShowCalculationConfirm] = useState(false);
    const [savingCalculations, setSavingCalculations] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    
    // Statistics state
    const [statistics, setStatistics] = useState({
        totalHeaders: 0,
        totalPoints: 0,
        maxDepth: 0,
        maxInclination: 0,
        maxDogleg: 0
    });

    const role = localStorage.getItem('role') || 'admin';

    // Load initial data
    useEffect(() => {
        loadWells();
        loadColumnConfigurations();
    }, []);

    // Load survey headers when well is selected
    useEffect(() => {
        if (selectedWellId) {
            loadSurveyHeaders();
            loadWellDetails();
        } else {
            setSurveyHeaders([]);
            setSelectedHeaderId('');
            setSelectedHeader(null);
            setSelectedWell(null);
        }
    }, [selectedWellId]);

    // Load survey points when header is selected and set table view by default
    useEffect(() => {
        if (selectedHeaderId && selectedWellId) {
            loadSurveyPoints();
            loadHeaderDetails();
            setCurrentView('table'); // Set table view by default
        } else {
            setSurveyPoints([]);
            setCalculatedData([]);
            setSelectedHeader(null);
        }
    }, [selectedHeaderId, selectedWellId]);

    // Calculate statistics when data changes
    useEffect(() => {
        calculateStatistics();
    }, [surveyPoints, surveyHeaders]);

    const loadWells = async () => {
        try {
            const response = await apiFetch('/api/wells');
            setWells(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error('Failed to load wells:', error);
            showToast('❌ Failed to load wells');
        }
    };

    const loadWellDetails = async () => {
        try {
            const response = await apiFetch(`/api/wells/${selectedWellId}`);
            setSelectedWell(response);
        } catch (error) {
            console.error('Failed to load well details:', error);
        }
    };

    const loadHeaderDetails = async () => {
        try {
            const response = await apiFetch(`/api/db/SurveyHeader/${selectedHeaderId}`);
            setSelectedHeader(response);
        } catch (error) {
            console.error('Failed to load header details:', error);
        }
    };

        const loadSurveyHeaders = async () => {
        try {
            const response = await apiFetch(`/api/db/SurveyHeader?filter=WellID,eq,${selectedWellId}`);
            setSurveyHeaders(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error('Failed to load survey headers:', error);
            showToast('❌ Failed to load survey headers');
        }
    };

    const loadColumnConfigurations = async () => {
        try {
            const response = await apiFetch('/api/config/columns');
            const allConfigs = Array.isArray(response) ? response : [];
            
            const surveyPointConfig = allConfigs.find(config => config.tableName === 'SurveyPoint');
            const surveyHeaderConfig = allConfigs.find(config => config.tableName === 'SurveyHeader');
            
            if (surveyPointConfig) setSurveyPointColumns(surveyPointConfig.columns.sort((a, b) => a.order - b.order));
            if (surveyHeaderConfig) setSurveyHeaderColumns(surveyHeaderConfig.columns.sort((a, b) => a.order - b.order));
        } catch (error) {
            console.error('Failed to load column configurations:', error);
            showToast('❌ Failed to load configurations');
        }
    };

    const loadSurveyPoints = async () => {
        setIsLoading(true);
        try {
            const response = await apiFetch(`/api/db/SurveyPoint?filter=SurveyID,eq,${selectedHeaderId}`);
            const data = Array.isArray(response) ? response : [];
            setSurveyPoints(data);
        
            // Check if calculations are needed
            const needsCalculation = data.some(row => 
                !row.Dx || !row.Dy || !row.TVD || 
                row.Dx === 0 || row.Dy === 0 || row.TVD === 0
            );
            
            if (needsCalculation && data.length > 0) {
                const calculated = calculateMissingValues(data);
                setCalculatedData(calculated);
                setShowCalculationConfirm(true);
            } else {
                setCalculatedData(data);
                setShowCalculationConfirm(false);
            }
        } catch (error) {
            console.error('Failed to load survey points:', error);
            showToast('❌ Failed to load survey points');
        }
        setIsLoading(false);
    };

    const calculateStatistics = () => {
        const stats = {
            totalHeaders: surveyHeaders.length,
            totalPoints: surveyPoints.length,
            maxDepth: surveyPoints.length > 0 ? Math.max(...surveyPoints.map(d => parseFloat(d.MD) || 0)) : 0,
            maxInclination: surveyPoints.length > 0 ? Math.max(...surveyPoints.map(d => parseFloat(d.Inclination) || 0)) : 0,
            maxDogleg: surveyPoints.length > 0 ? Math.max(...surveyPoints.map(d => parseFloat(d.DogLeg) || 0)) : 0
        };
        setStatistics(stats);
    };

    // Calculate missing Dx, Dy, TVD values
    const calculateMissingValues = (data) => {
        const sortedData = [...data].sort((a, b) => (a.MD || 0) - (b.MD || 0));
        const calculated = [];
        
        let cumDx = 0, cumDy = 0, cumTVD = 0;
        
        for (let i = 0; i < sortedData.length; i++) {
            const current = { ...sortedData[i] };
            const depth = parseFloat(current.MD) || 0;
            const inclination = parseFloat(current.Inclination) || 0;
            const azimuth = parseFloat(current.Azimuth) || 0;
            
            if (i === 0) {
                current.TVD = current.TVD || depth;
                current.Dx = current.Dx || 0;
                current.Dy = current.Dy || 0;
                cumTVD = current.TVD;
                cumDx = current.Dx;
                cumDy = current.Dy;
            } else {
                const prev = calculated[i - 1];
                const prevDepth = parseFloat(prev.MD) || 0;
                const prevInc = parseFloat(prev.Inclination) || 0;
                const prevAz = parseFloat(prev.Azimuth) || 0;
                
                const deltaDepth = depth - prevDepth;
                
                if (deltaDepth > 0) {
                    const avgInc = (inclination + prevInc) / 2;
                    const avgAz = (azimuth + prevAz) / 2;
                    
                    const incRad = (avgInc * Math.PI) / 180;
                    const azRad = (avgAz * Math.PI) / 180;
                    
                    const deltaTVD = deltaDepth * Math.cos(incRad);
                    const deltaHoriz = deltaDepth * Math.sin(incRad);
                    const deltaDx = deltaHoriz * Math.sin(azRad);
                    const deltaDy = deltaHoriz * Math.cos(azRad);
                    
                    cumTVD += deltaTVD;
                    cumDx += deltaDx;
                    cumDy += deltaDy;
                }
                
                current.TVD = current.TVD || cumTVD;
                current.Dx = current.Dx || cumDx;
                current.Dy = current.Dy || cumDy;
                
                if (!current.DogLeg && i > 0) {
                    const prevIncRad = (prevInc * Math.PI) / 180;
                    const currIncRad = (inclination * Math.PI) / 180;
                    const azDiffRad = ((azimuth - prevAz) * Math.PI) / 180;
                    
                    const doglegRad = Math.acos(
                        Math.cos(currIncRad) * Math.cos(prevIncRad) +
                        Math.sin(currIncRad) * Math.sin(prevIncRad) * 
                        Math.cos(azDiffRad)
                    );
                    current.DogLeg = (doglegRad * 180 / Math.PI) / (deltaDepth / 100);
                }
            }
            
            current.TVD = Math.round((current.TVD || 0) * 100) / 100;
            current.Dx = Math.round((current.Dx || 0) * 100) / 100;
            current.Dy = Math.round((current.Dy || 0) * 100) / 100;
            current.DogLeg = Math.round((current.DogLeg || 0) * 100) / 100;
            
            calculated.push(current);
        }
        
        return calculated;
    };

    const handleWellChange = (e) => {
        setSelectedWellId(e.target.value);
        setSelectedHeaderId('');
        setSurveyPoints([]);
        setCalculatedData([]);
    };

    const handleHeaderChange = (e) => {
        setSelectedHeaderId(e.target.value);
    };

    // Handle survey header selection from dropdown
    const handleSelectSurveyHeader = (header) => {
        setSelectedHeaderId(header.SurveyID);
        // Optionally show a toast to indicate selection
        showToast(`📊 Selected survey: ${header.SurveyName || `Survey ${header.SurveyID}`}`);
    };

    const showToast = (message) => {
        setToast(message);
        setTimeout(() => setToast(''), 3000);
    };

    const handleDataChange = async () => {
        if (selectedHeaderId && selectedWellId) {
            await loadSurveyPoints();
        }
        if (selectedWellId) {
            await loadSurveyHeaders();
        }
    };



    // Enhanced Add Record Handler
    const handleAddRecord = (tableName, defaultData = {}, formType = 'single') => {
        let config = {};
        let data = { ...defaultData };
        
        if (tableName === 'SurveyHeader') {
            data.WellID = selectedWellId;
            config = {
                formType: 'single',
                tableName: 'SurveyHeader',
                data,
                title: 'Add New Survey Header'
            };
        } else if (tableName === 'SurveyPoint') {
            data.SurveyID = selectedHeaderId;
            config = {
                formType: 'single',
                tableName: 'SurveyPoint',
                data,
                title: 'Add New Survey Point'
            };
        }
        
        setDataFormConfig(config);
        setEditingRecord(null);
        setShowDataForm(true);
        setSelectedSection(tableName);
        setSelectedRecord(null);
    };

    // New Dual Table Handler for Deviation Survey
    const handleAddDeviationSurvey = () => {
        if (!selectedWellId) {
            showToast('❌ Please select a well first');
            return;
        }

        const config = {
            tables: { parent: 'SurveyHeader', child: 'SurveyPoint' },
            identityColumns: { parent: 'SurveyID', child: 'PointID' },
            matchKey: { parentKey: 'SurveyID', childForeignKey: 'SurveyID' },
            defaultParentData: { WellID: selectedWellId },
            hiddenParentFields: ['SurveyID', 'CreatedAt', 'UpdatedAt'],
            title: 'Add New Deviation Survey'
        };
        
        setDualTableConfig(config);
        setShowDualTableForm(true);
        setShowDataForm(false);
    };

    // Enhanced Edit Record Handler
    const handleEditRecord = (tableName, record, formType = 'single') => {
        let config = {};
        
        if (tableName === 'SurveyHeader') {
            config = {
                formType: 'single',
                tableName: 'SurveyHeader',
                data: { ...record },
                title: 'Edit Survey Header'
            };
        } else if (tableName === 'SurveyPoint') {
            config = {
                formType: 'single',
                tableName: 'SurveyPoint',
                data: { ...record },
                title: 'Edit Survey Point'
            };
        }
        
        setDataFormConfig(config);
        setEditingRecord(record);
        setShowDataForm(true);
        setSelectedSection(tableName);
        setSelectedRecord(record);
    };

    // Cancel handler for DataForm
    const handleCancelForm = () => {
        setShowDataForm(false);
        setShowDualTableForm(false);
        setEditingRecord(null);
        setDataFormConfig({});
        setDualTableConfig({});
        setSelectedSection(null);
        setSelectedRecord(null);
    };

    // DataForm Save Handler
    const handleDataFormSave = useCallback(async (result) => {
        try {
            console.log('✅ Data saved successfully:', result);
            showToast('✅ Data saved successfully');
            
            setShowDataForm(false);
            setShowDualTableForm(false);
            setEditingRecord(null);
            setDataFormConfig({});
            setDualTableConfig({});
            setSelectedSection(null);
            setSelectedRecord(null);
            
            await handleDataChange();
        } catch (error) {
            console.error('❌ Failed to save data:', error);
            showToast('❌ Failed to save data: ' + error.message);
        }
    }, [dataFormConfig, dualTableConfig]);

    const handleDeleteRecord = async (tableName, record) => {
        if (!window.confirm('Are you sure you want to delete this record?')) {
            return;
        }

        try {
            let primaryKey = 'ID';
            if (tableName === 'SurveyHeader') primaryKey = 'SurveyID';
            else if (tableName === 'SurveyPoint') primaryKey = 'PointID';

            await apiFetch(`/api/db/${tableName}/${record[primaryKey]}`, {
                method: 'DELETE'
            });

            showToast('✅ Record deleted successfully');
            await handleDataChange();
        } catch (error) {
            console.error('Failed to delete record:', error);
            showToast('❌ Failed to delete record');
        }
    };

    const handleSaveCalculations = async () => {
        if (!selectedHeaderId || !calculatedData.length) return;
        
        setSavingCalculations(true);
        try {
            const response = await apiFetch(`/api/survey/batch-update`, {
                method: 'POST',
                body: JSON.stringify({
                    updates: calculatedData
                })
            });
            
            if (response.success || response.ok) {
                showToast('✅ Calculations saved successfully!');
                setShowCalculationConfirm(false);
                await loadSurveyPoints();
            } else {
                throw new Error(response.message || 'Failed to save calculations');
            }
        } catch (err) {
            console.error('Failed to save calculations:', err);
            showToast('❌ Failed to save calculations: ' + err.message);
        } finally {
            setSavingCalculations(false);
        }
    };

    // Delete survey (header + all points)
    const handleDeleteSurvey = async () => {
        if (!selectedHeaderId) {
            showToast('❌ Please select a survey to delete');
            return;
        }

        const surveyName = selectedHeader?.SurveyName || `Survey ${selectedHeaderId}`;
        if (!window.confirm(`Are you sure you want to delete "${surveyName}" and all its survey points? This action cannot be undone.`)) {
            return;
        }

        try {
            setIsLoading(true);
            
            // First, get all survey points for this survey
            const surveyPointsResponse = await apiFetch(`/api/db/SurveyPoint?filter=SurveyID,eq,${selectedHeaderId}`);
            const pointsToDelete = Array.isArray(surveyPointsResponse) ? surveyPointsResponse : [];
            
            // Delete each survey point individually if any exist
            if (pointsToDelete.length > 0) {
                for (const point of pointsToDelete) {
                    await apiFetch(`/api/db/SurveyPoint/${point.PointID}`, {
                        method: 'DELETE'
                    });
                }
            }
            
            // Then delete the survey header
            await apiFetch(`/api/db/SurveyHeader/${selectedHeaderId}`, {
                method: 'DELETE'
            });

            showToast('✅ Survey deleted successfully');
            
            // Reset selections and reload data
            setSelectedHeaderId('');
            setSelectedHeader(null);
            setSurveyPoints([]);
            setCalculatedData([]);
            await loadSurveyHeaders();
        } catch (error) {
            console.error('Failed to delete survey:', error);
            showToast('❌ Failed to delete survey: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Toggle full screen mode
    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
    };

    const renderWellHeaderSelector = () => (
        <div className="well-header-selector">
            <div className="selector-row">
                <div className="selector-group">
                    <label htmlFor="well-select">Well:</label>
                    <select
                        id="well-select"
                        value={selectedWellId}
                        onChange={handleWellChange}
                        className="form-select"
                    >
                        <option value="">Select a well...</option>
                        {wells.map(well => (
                            <option key={well.WellID} value={well.WellID}>
                                {well.WellName}
                            </option>
                        ))}
                    </select>
                </div>
                
                <div className="selector-group">
                    <label htmlFor="header-select">Survey Header:</label>
                    <select
                        id="header-select"
                        value={selectedHeaderId}
                        onChange={handleHeaderChange}
                        className="form-select"
                        disabled={!selectedWellId}
                    >
                        <option value="">Select a survey header...</option>
                        {surveyHeaders.map(header => (
                            <option key={header.SurveyID} value={header.SurveyID}>
                                {header.SurveyName || `Survey ${header.SurveyID}`}
                                {header.SurveyDate && ` - ${new Date(header.SurveyDate).toLocaleDateString()}`}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );



    const renderQuickActions = () => (
        <div className="quick-actions">
            <button
                className="quick-action-btn small primary-action"
                onClick={() => handleAddDeviationSurvey()}
                title="Add Complete Deviation Survey (Header + Points)"
                disabled={!selectedWellId}
            >
                🛠️ Add Survey
            </button>
            <button
                className="quick-action-btn small danger-action"
                onClick={() => handleDeleteSurvey()}
                title="Delete Selected Survey and All Points"
                disabled={!selectedHeaderId}
            >
                🗑️ Delete Survey
            </button>
            <button
                className={`quick-action-btn small ${isFullScreen ? 'active' : ''}`}
                onClick={() => toggleFullScreen()}
                title={isFullScreen ? "Exit Full Screen" : "Full Screen View"}
            >
                {isFullScreen ? '🗗' : '🗖'} {isFullScreen ? 'Exit' : 'Full'}
            </button>
        </div>
    );



    const renderRightPanel = () => (
        <div className={`bha-details-panel ${isFullScreen ? 'fullscreen-panel' : ''}`}>
            {showDataForm ? (
                <div className="form-container">
                    <DataForm
                        data={dataFormConfig.data}
                        tableName={dataFormConfig.tableName}
                        columnsPerRow={2}
                        onSave={handleDataFormSave}
                        onCancel={handleCancelForm}
                        title={dataFormConfig.title}
                    />
                </div>
            ) : showDualTableForm ? (
                <div className="form-container">
                    <DataForm
                        tables={dualTableConfig.tables}
                        identityColumns={dualTableConfig.identityColumns}
                        matchKey={dualTableConfig.matchKey}
                        defaultParentData={dualTableConfig.defaultParentData}
                        hiddenParentFields={dualTableConfig.hiddenParentFields}
                        columnsPerRow={2}
                        onSave={handleDataFormSave}
                        onCancel={handleCancelForm}
                        title={dualTableConfig.title}
                    />
                </div>
            ) : (
                <div className={`view-content ${isFullScreen ? 'fullscreen-view' : ''}`}>
                    {surveyPoints.length > 0 ? (
                        <>
                            <div className="view-tabs">
                                <button
                                    className={`tab-btn ${currentView === 'table' ? 'active' : ''}`}
                                    onClick={() => setCurrentView('table')}
                                >
                                    📋 Table
                                </button>
                                <button
                                    className={`tab-btn ${currentView === '2d' ? 'active' : ''}`}
                                    onClick={() => setCurrentView('2d')}
                                >
                                    📈 2D
                                </button>
                                <button
                                    className={`tab-btn ${currentView === '3d' ? 'active' : ''}`}
                                    onClick={() => setCurrentView('3d')}
                                >
                                    🎯 3D
                                </button>
                            </div>
                            
                            <div className="tab-content">
                                {currentView === 'table' && (
                                    <div className="data-table-container">
                                        <DataTable
                                            data={calculatedData.length > 0 ? calculatedData : surveyPoints}
                                            columns={surveyPointColumns}
                                            itemsPerPage={isFullScreen ? 50 : 20}
                                            title={isFullScreen ? "" : "Survey Points"}
                                            tableName="SurveyPoint"
                                            onDataChange={handleDataChange}
                                        />
                                    </div>
                                )}
                                
                                {currentView === '2d' && (
                                    <div className="visualization-container">
                                        <WellPath2D data={calculatedData.length > 0 ? calculatedData : surveyPoints} />
                                    </div>
                                )}
                                
                                {currentView === '3d' && (
                                    <div className="visualization-container">
                                        <WellPath3D data={calculatedData.length > 0 ? calculatedData : surveyPoints} />
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="placeholder-container">
                            <div className="placeholder-content">
                                <div className="placeholder-icon">📍</div>
                                <p>Select a survey header to view survey points or add new points using the form.</p>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    if (isLoading) {
        return (
            <div className="wellbha-container loading">
                <div className="loading-spinner">Loading...</div>
            </div>
        );
    }

    return (
        <div className={`wellbha-container ${isFullScreen ? 'fullscreen-mode' : 'fullscreen'}`}>
            {!isFullScreen && (
                <div className="wellbha-header">
                    <div className="header-main">
                        <h1>🛠️ Well Deviation</h1>
                        {renderWellHeaderSelector()}
                    </div>
                    {renderQuickActions()}
                </div>
            )}
            
            {isFullScreen && (
                <div className="fullscreen-header">
                    <div className="fullscreen-title">
                        <h2>📊 {selectedHeader?.SurveyName || `Survey ${selectedHeaderId}`} - Survey Points</h2>
                        {selectedWell && (
                            <span className="well-context">
                                {selectedWell.WellName} • {surveyPoints.length} points
                            </span>
                        )}
                    </div>
                    <div className="fullscreen-controls">
                        {renderQuickActions()}
                    </div>
                </div>
            )}
            
            {selectedWellId && (
                <div className={`wellbha-content-single ${isFullScreen ? 'fullscreen-content' : ''}`}>
                    {renderRightPanel()}
                </div>
            )}

            {/* Calculation Confirmation Modal */}
            {showCalculationConfirm && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>⚠️ Missing Calculations Detected</h3>
                            <button className="close-btn" onClick={() => setShowCalculationConfirm(false)}>✕</button>
                        </div>
                        <div className="modal-body">
                            <p>Some Dx, Dy, or TVD values are missing. Calculated values have been generated using the average angle method.</p>
                            <div className="calculation-buttons">
                                <button 
                                    onClick={handleSaveCalculations}
                                    disabled={savingCalculations}
                                    className="save-calculations-btn"
                                >
                                    {savingCalculations ? 'Saving...' : '✅ Save Calculated Values'}
                                </button>
                                <button 
                                    onClick={() => setShowCalculationConfirm(false)}
                                    className="dismiss-calculations-btn"
                                >
                                    ❌ Use Original Data
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Message */}
            {toast && (
                <div className="toast-message">
                    {toast}
                </div>
            )}
        </div>
    );
};

export default WellDeviation;
